import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiTokensApi, type APITokenCreated } from '../api'
import { useSession } from '../session'
import { t } from '../i18n'
import { Button, Notice } from '../components/ui'
import { CreateForm, ShownOnce, TokenList, type CreateInput } from './ApiTokensParts'

/*
 * Account → API tokens (docs/api-tokens.md §8.1): the user's tokens (sxk_),
 * creating one (shown once) and revoking. A plan without API tokens
 * (available: false) still lists the old ones so they can be revoked.
 */

export const API_TOKENS_KEY = ['me', 'api-tokens'] as const

/** The cabinet's name: user tokens work there (not on the console name). */
export function cabinetBase(appURL?: string, area?: string): string {
  if (area === 'admin' && appURL) return appURL.replace(/\/+$/, '')
  return location.origin
}

export function ApiTokensPage() {
  const qc = useQueryClient()
  const { user } = useSession()
  const q = useQuery({ queryKey: API_TOKENS_KEY, queryFn: apiTokensApi.list })
  const [form, setForm] = useState(false)
  // The secret: here only, while the panel is open.
  const [created, setCreated] = useState<APITokenCreated | null>(null)

  const refresh = () => qc.invalidateQueries({ queryKey: API_TOKENS_KEY })
  const create = async (b: CreateInput) => {
    const r = await apiTokensApi.create(b)
    setForm(false)
    setCreated(r)
    await refresh()
  }
  const revoke = async (id: number) => {
    await apiTokensApi.revoke(id)
    await refresh()
  }

  const data = q.data
  const tokens = data?.tokens ?? []
  const full = !!data && tokens.length >= data.limit
  return (
    <div data-testid="api-tokens-page" className="flex max-w-3xl flex-col gap-6">
      <div className="border-b border-line pb-5">
        <h1 className="m-0 text-ui-heading text-text">{t('apiTokens.title')}</h1>
        <p className="mt-2 mb-0 text-ui-body text-text-soft">{t('apiTokens.intro')}</p>
      </div>
      {created && <ShownOnce created={created} base={cabinetBase(user?.app_url, user?.area)} path="/api/me" onClose={() => setCreated(null)} />}
      {q.isPending ? (
        <p className="text-sm text-muted">{t('apiTokens.loading')}</p>
      ) : q.isError || !data ? (
        <div className="flex flex-wrap items-center gap-3">
          <Notice>{t('apiTokens.failed')}</Notice>
          <Button onClick={() => q.refetch()}>{t('apiTokens.retry')}</Button>
        </div>
      ) : (
        <>
          {!data.available ? (
            <div className="flex flex-col gap-2" data-testid="api-tokens-plan">
              <Notice kind="info">{t('apiTokens.plan')}</Notice>
              {tokens.length > 0 && <p className="m-0 text-sm text-muted">{t('apiTokens.planExisting')}</p>}
            </div>
          ) : form ? (
            <CreateForm list={data} onCreate={create} onCancel={() => setForm(false)} />
          ) : (
            !created && (
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="primary" disabled={full} onClick={() => setForm(true)} data-testid="api-tokens-create" className="max-md:min-h-[44px]">
                  {t('apiTokens.create')}
                </Button>
                <span className={`text-sm ${full ? 'text-warn' : 'text-muted'}`} data-testid="api-tokens-count">
                  {full ? t('apiTokens.limit', { n: tokens.length, max: data.limit }) : t('apiTokens.count', { n: tokens.length, max: data.limit })}
                </span>
              </div>
            )
          )}
          {tokens.length === 0 ? (
            !form && (
              <div data-testid="api-tokens-empty" className="flex flex-col gap-2 rounded-[0.5rem] border border-dashed border-line-2 p-4">
                <p className="m-0 font-medium text-text">{t('apiTokens.emptyTitle')}</p>
                <p className="m-0 text-sm text-muted">{t('apiTokens.empty')}</p>
                <code className="break-all font-mono text-xs text-text-soft">Authorization: Bearer sxk_…</code>
              </div>
            )
          ) : (
            <TokenList tokens={tokens} onRevoke={revoke} />
          )}
        </>
      )}
    </div>
  )
}
